import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef, EventEmitter, HostListener,
  OnDestroy,
  OnInit, Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { InteractionService } from '../../../services/interaction.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { MatSelectChange } from '@angular/material/select';
import { SettingsService } from '../../../services/settings.service';
import { DefinitionOfDoneService } from '../../../services/definition-of-done.service';
import { ProjectService } from '../../../services/project.service';
import { DefinitionOfDone } from '../../../models/definitionOfDone';
import { definitionOfDoneDisplayType } from '../../../models/enums/dodDisplayType';
import { PopupDodComponent } from '../../popup-dod/popup-dod.component';
import { Issue } from '../../../models/issue';
import interact from 'interactjs';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { IWidget } from '../../../interfaces/iWidget';

@Component({
  selector: 'app-definition-of-done-widget',
  templateUrl: './definition-of-done-widget.component.html',
  styleUrls: ['./definition-of-done-widget.component.scss']
  })

export class DefinitionOfDoneWidgetComponent implements IWidget, OnInit, OnDestroy {
  static widgetName = 'definitionOfDone';
  languages: string[];
  private projectName: string;
  private projectId: number;
  public definitionOfDones: DefinitionOfDone[];
  public definitionOfDoneActive: boolean;
  public definitionOfDoneDisplayType: definitionOfDoneDisplayType;
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  @ViewChild('dodPopupInsertionPoint', { read: ViewContainerRef, static: true }) popupInsertionPoint: ViewContainerRef;
  private interactedClass = 'resizedwidget';
  private event: Subscription[] = [];
  public editMode = false;
  public newDoDValue: string;

  public popupCards: PopupDodComponent[] = [];

  // style
  public zIndex = 25;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(private translate: TranslateService,
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private settingsService: SettingsService,
    private definitionOfDoneService: DefinitionOfDoneService,
    private projectService: ProjectService,
    private resolver: ComponentFactoryResolver,
    private positionHelper: HTMLElementPositioningHelper) {
    this.languages = this.translate.getLangs();
  }

  changeLanguage(event: MatSelectChange) {
    this.translate.use(event.value);
  }

  ngOnInit() {
    this.setupRealTimeEvents();
    this.setupinteraction();
    this.event.push(this.settingsService.getUpdateObservable().subscribe(res => {
      this.loadInfo();
    }));

    this.definitionOfDoneService.newDoDPopUpObserver.subscribe(res => {
      const popup: PopupDodComponent = this.dynamicallyAddPopupComponent(res);
      const popupElement = popup.getElementRef().nativeElement;
      popupElement.cardObject = popup;

      this.synchronization.sendRealTimeEvent('OpenPopup',
        {
          'issue': popup.issue,
          'x': popup.getElementRef().nativeElement.getAttribute('data-x'),
          'y': popup.getElementRef().nativeElement.getAttribute('data-y')
        }
      );
    });

    this.definitionOfDoneService.removeDoDPopUp.subscribe(issue => {
      this.dynamicallyRemovePopupComponent(issue.id);
    });
    this.loadInfo();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
    // Cleanup interactjs-listeners
    this.interactionService.unset('.definitionOfDoneWidget');
  }

  loadInfo() {
    this.projectName = this.settingsService.getProjectName();
    this.projectService.getProjectByKey(this.projectName).subscribe(val => {
      this.projectId = val.id;
      // set definition of done active
      if (val['definition_of_done_active'] == null) {
        this.definitionOfDoneActive = false;
      } else {
        this.definitionOfDoneActive = val['definition_of_done_active'];
      }

      // set definition of done version, default popup
      if (val['definition_of_done_version'] === 'issueCard') {
        this.definitionOfDoneDisplayType = definitionOfDoneDisplayType.issueCard;
      } else {
        this.definitionOfDoneDisplayType = definitionOfDoneDisplayType.popup;
      }
      this.loadData();
    });
  }

  loadData() {
    if (this.projectId) {
      this.definitionOfDoneService.getDefinitionOfDone(this.projectId, false).subscribe(value => {
        this.definitionOfDones = [];
        this.definitionOfDones = value;
      });
    }
  }

  private setupRealTimeEvents() {
    const obj = this;
    this.event.push(this.synchronization.subscribeEvent<any>('DefinitionOfDoneWidgetResizeWidget')
      .subscribe(size => {
        const target = obj.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(DefinitionOfDoneWidgetComponent.widgetName + 'StartMoveWidget')
      .subscribe(() => {
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(DefinitionOfDoneWidgetComponent.widgetName + 'MoveWidget')
      .subscribe(positionRelative => {
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);
        this.positionHelper.setRelativePosition(target, positionRelative.left, positionRelative.top, null, scale);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(DefinitionOfDoneWidgetComponent.widgetName + 'EndMoveWidget')
      .subscribe(() => {
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
        target.style.width = 'auto';
        target.style.height = 'auto';
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(DefinitionOfDoneWidgetComponent.widgetName + 'ResetWidget')
      .subscribe(() => {
        obj.resetWidget(obj.element.nativeElement);
      }));

    // Popup Events
    this.synchronization.subscribeEvent('OpenPopup').subscribe(data => {
      const issue: Issue = data['issue'];
      if (!issue) {
        return;
      }
      const popup: PopupDodComponent = this.dynamicallyAddPopupComponent(issue);
      const popupElement = popup.getElementRef().nativeElement;
      popupElement.cardObject = popup;
    });

    this.synchronization.subscribeEvent('ClosePopup').subscribe(issue => {
      this.dynamicallyRemovePopupComponent(Number(issue['id']));
    });

    this.synchronization.subscribeEvent('PopupMoved').subscribe(data => {
      const popup = this.getPopup(data['issueId']);

      if (popup) {
        const target = popup.getElementRef().nativeElement;
        const translateX = document.documentElement.clientWidth * data['percentFromLeft'] / 100;
        const translateY = (document.documentElement.clientHeight * data['percentFromTop'] / 100);

        target.style.webkitTransform = 'translate(' + translateX + 'px, ' + translateY + 'px)';
        target.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px)';
        target.setAttribute('data-x', '' + translateX);
        target.setAttribute('data-y', '' + translateY);
      }
    });
  }

  private setupinteraction() {
    this.interactionService.addDragInteraction('.definitionOfDoneWidget', (event) => {
      event.target.classList.add(this.interactedClass);
      this.synchronization.sendRealTimeEvent(DefinitionOfDoneWidgetComponent.widgetName + 'StartMoveWidget');
    }, (positionAbsolute) => {
      const relativePosition = this.positionHelper.getRelativePosition(positionAbsolute.x, positionAbsolute.y);
      this.synchronization.sendRealTimeEvent(DefinitionOfDoneWidgetComponent.widgetName + 'MoveWidget', relativePosition);
    }, (event) => {
      const target = this.element.nativeElement;
      target.style.width = 'auto';
      target.style.height = 'auto';
      this.synchronization.sendRealTimeEvent(DefinitionOfDoneWidgetComponent.widgetName + 'EndMoveWidget');
    });
  }

  addNewDoD(): void {
    const newDefinitionOfDone: DefinitionOfDone = new DefinitionOfDone(null, this.projectId, this.newDoDValue, false, false);
    if (this.projectId && this.newDoDValue !== '') {
      this.definitionOfDoneService.addDefinitionOfDone(this.projectId, newDefinitionOfDone).subscribe(val => {
        this.definitionOfDones.push(val);
      });
    }
    this.newDoDValue = '';
  }

  leaveEditMode(): void {
    this.editMode = false;
  }


  deleteDoD(dod: DefinitionOfDone) {
    this.definitionOfDoneService.deleteDefinitionOfDone(this.projectId, dod).subscribe(value => {
      const singleDoD = this.definitionOfDones.find(x => x.id === value.id);
      singleDoD.isDeleted = value.isDeleted;
      const index: number = this.definitionOfDones.indexOf(singleDoD);
      if (index !== -1) {
        this.definitionOfDones.splice(index, 1);
      }
    });
  }

  changeActive(id: number) {
    const changedDefinitionOfDone = this.definitionOfDones.find(x => x.id === id);
    changedDefinitionOfDone.active = !changedDefinitionOfDone.active;
    this.definitionOfDoneService.updateDefinitionOfDone(this.projectId, changedDefinitionOfDone);
  }

  changeProjectActive() {
    this.definitionOfDoneActive = !this.definitionOfDoneActive;
    this.projectService.getProjectById(this.projectId).subscribe(value => {
      value['definition_of_done_active'] = this.definitionOfDoneActive;
      this.projectService.updateProjectDoD(value).subscribe();
    });
  }

  versionChanged(version: definitionOfDoneDisplayType) {
    this.definitionOfDoneDisplayType = version;
    this.projectService.getProjectById(this.projectId).subscribe(value => {
      value['definition_of_done_version'] = this.definitionOfDoneDisplayType;
      this.projectService.updateProjectDoD(value).subscribe();
    });
  }

  getAllVersion() {
    return Object.keys(definitionOfDoneDisplayType);
  }

  changeName(id: number, newName: string) {

    const changedDefinitionOfDone = this.definitionOfDones.find(x => x.id === id);
    changedDefinitionOfDone.name = newName;
    this.definitionOfDoneService.updateDefinitionOfDone(this.projectId, changedDefinitionOfDone);
    this.editMode = false;
    const button = document.getElementById('button-dod-' + id);
    button.style.display = 'none';
  }

  onChangeText(id: number) {
    const button = document.getElementById(`button-dod-${id}`);
    if (button) {
      if (button.style.display === 'none') {
        button.style.display = 'block';
      }
    }
  }

  reset(event: any) {
    this.synchronization.sendRealTimeEvent(DefinitionOfDoneWidgetComponent.widgetName + 'ResetWidget');
    const closest = event.target.closest(`.${this.interactedClass}`);
    this.resetWidget(closest);
  }

  resetWidget(target: HTMLElement) {
    this.interactionService.undoDragInteraction(target);
    target.classList.remove(this.interactedClass);
    this.editMode = false;
    this.newDoDValue = '';
  }

  /**
   * Below here are functions for DoD Popups
   **/
  dynamicallyAddPopupComponent(issue: Issue): PopupDodComponent {
    const componentRef: ComponentRef<PopupDodComponent> = this.popupInsertionPoint.createComponent(PopupDodComponent);
    const popup: PopupDodComponent = componentRef.instance;
    popup._issue = issue;

    this.popupCards.push(popup);
    this.setupPopupAsDraggable(popup);
    popup.getElementRef().nativeElement.style.zIndex = '5';
    popup.getElementRef().nativeElement.style.top = '50%';
    popup.getElementRef().nativeElement.style.left = '50%';

    return popup;
  }

  setupPopupAsDraggable(popupCard: PopupDodComponent) {
    // a custom function that gets executed when a 'onMove'-Event occurs on the draggable element
    const onMoveSendSyncEvent = (event: any): void => {
      const target = event.target;
      const x = Number(target.getAttribute('data-x') || 0) + event.dx;
      const y = Number(target.getAttribute('data-y') || 0) + event.dy;
      const percentFromTop = y / (document.documentElement.clientHeight / 100.0);
      const percentFromLeft = x / (document.documentElement.clientWidth / 100.0);

      const data = {
        'issueId': popupCard._issue.id,
        'x': x,
        'y': y,
        'percentFromTop': percentFromTop,
        'percentFromLeft': percentFromLeft
      };
      this.synchronization.sendRealTimeEvent('PopupMoved', data);
    };
    const popupCardElement: HTMLElement = popupCard.getElementRef().nativeElement;
    this.interactionService.setUpDraggable(popupCardElement, popupCardElement, onMoveSendSyncEvent);
  }

  dynamicallyRemovePopupComponent(issueId: number) {
    const popup = this.getPopup(issueId);
    try {
      interact(popup.getElementRef().nativeElement).unset();
    } catch {
      // Nothing to do
    }
    const popupIndex = this.popupCards.indexOf(popup);

    // Remove the card from issueCards-array
    this.popupCards.splice(popupIndex, 1);
    // Remove the card from the dynamic container
    this.popupInsertionPoint.remove(popupIndex);
  }

  getPopup(issueId: number): PopupDodComponent {
    const result: PopupDodComponent = this.popupCards.find(popup => Number(popup._issue.id) === Number(issueId));
    return result;
  }

  setPopupPosition(popup: PopupDodComponent, positionX: number, positionY: number, remote?: boolean) {
    if (!popup) { return; }

    const popupElement = popup.getElementRef().nativeElement;
    const transformX = (remote) ? positionX : positionX - popupElement.getBoundingClientRect().left;
    const transformY = (remote) ? positionY : positionY - popupElement.getBoundingClientRect().top;
    const transformString = 'translate(' + transformX + 'px, ' + transformY + 'px)';

    popupElement.style.transform = transformString;
    popupElement.style.webkitTransform = transformString;
    popupElement.setAttribute('data-x', transformX);
    popupElement.setAttribute('data-y', transformY);
    popupElement.cardObject = popup;
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
